/**
 * Manual text browser for a selected pandora item. Surfaces the item's
 * description, keywords and time-aligned annotation layers (subtitles /
 * transcripts + others), each with a click-to-insert action that creates an
 * editable timeline text clip via the timeline store. No auto-sync.
 */
import { useEffect, useMemo, useState } from 'react';
import { archiveLayerMap, type Archive } from '../api/archives';
import {
  getItemText,
  getLayers,
  normalizeKeywords,
  stripHtml,
  type PandoraAnnotation,
  type PandoraLayers,
} from '../api/pandora';
import { formatTime } from '../format/helpers';
import { concatTimeToTimeline, presetStyle, type TextPreset } from '../format/textInsert';
import { usePlayback } from '../state/playback';
import { useTimeline } from '../state/timeline';
import './ItemTextPanel.css';

interface Props {
  archive: Archive;
  itemId: string;
  /** concat-time offset at the start of each part (0-based) */
  partOffsets: number[];
}

const ROWS_COLLAPSED = 8;

export default function ItemTextPanel({ archive, itemId, partOffsets }: Props) {
  const [summary, setSummary] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [layers, setLayers] = useState<PandoraLayers>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const map = useMemo(() => archiveLayerMap(archive.key), [archive.key]);

  useEffect(() => {
    let alive = true;
    setSummary('');
    setKeywords([]);
    setLayers({});
    setError(null);
    setLoading(true);
    Promise.all([
      getItemText(archive.site, itemId).catch(() => ({ summary: '', keywords: [] as string[] })),
      getLayers(archive.site, itemId).catch(() => ({} as PandoraLayers)),
    ]).then(
      ([text, lyr]) => {
        if (!alive) return;
        setSummary(text.summary);
        // Keywords come from item metadata and/or keyword-type layers.
        const fromLayers: string[] = [];
        for (const layerId of map.keywordLayers)
          for (const a of lyr[layerId] ?? []) fromLayers.push(...normalizeKeywords(a.value));
        setKeywords(Array.from(new Set([...text.keywords, ...fromLayers])));
        setLayers(lyr);
        setLoading(false);
      },
      (e) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : 'failed to load text');
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [archive.site, archive.key, itemId, map]);

  function insert(text: string, preset: TextPreset, concatIn?: number, concatOut?: number) {
    const clean = stripHtml(text);
    if (!clean) return;
    const p = presetStyle(preset);
    const edit = useTimeline.getState().edit;
    const playhead = usePlayback.getState().currentTime;

    let start = playhead;
    let duration = p.duration;
    if (concatIn !== undefined) {
      const mapped = concatTimeToTimeline(edit, archive.key, itemId, partOffsets, concatIn);
      if (mapped !== null) start = mapped;
      if (concatOut !== undefined && concatOut > concatIn)
        duration = Math.max(0.5, concatOut - concatIn);
    }
    useTimeline.getState().addTextClipFrom({
      text: clean,
      start,
      duration,
      style: p.style,
      position: p.position,
      fadeIn: p.fadeIn,
      fadeOut: p.fadeOut,
    });
  }

  // Time-aligned layers: subtitle/transcript layers first, then other in/out
  // layers present on the item (excluding keyword layers, shown as chips).
  const subtitleLayerIds = map.subtitleLayers.filter((id) => (layers[id]?.length ?? 0) > 0);
  const otherLayerIds = Object.keys(layers).filter(
    (id) =>
      (layers[id]?.length ?? 0) > 0 &&
      !map.subtitleLayers.includes(id) &&
      !map.keywordLayers.includes(id),
  );
  const orderedOther = [
    ...map.layers.filter((id) => otherLayerIds.includes(id)),
    ...otherLayerIds.filter((id) => !map.layers.includes(id)),
  ];

  return (
    <div className="item-text">
      <h4 className="item-text-head">Text</h4>
      {loading && <div className="search-hint">loading text…</div>}
      {error && <div className="item-detail-error">{error}</div>}

      {!loading && summary && (
        <section className="item-text-section">
          <div className="item-text-label">Description</div>
          <p className="item-text-desc">{stripHtml(summary)}</p>
          <button className="item-text-add" onClick={() => insert(summary, 'title')}>
            + Add as text
          </button>
        </section>
      )}

      {!loading && keywords.length > 0 && (
        <section className="item-text-section">
          <div className="item-text-label">Keywords</div>
          <div className="item-text-chips">
            {keywords.map((k) => (
              <button
                key={k}
                className="item-text-chip"
                title="Add as text at playhead"
                onClick={() => insert(k, 'keyword')}
              >
                {k}
              </button>
            ))}
          </div>
        </section>
      )}

      {subtitleLayerIds.map((id) => (
        <LayerSection
          key={id}
          title={layerTitle(id)}
          rows={layers[id]}
          preset="subtitle"
          onInsert={insert}
        />
      ))}

      {orderedOther.map((id) => (
        <LayerSection
          key={id}
          title={layerTitle(id)}
          rows={layers[id]}
          preset="title"
          onInsert={insert}
        />
      ))}

      {!loading && !error && !summary && !keywords.length && !subtitleLayerIds.length && !orderedOther.length && (
        <div className="search-hint">no text for this item</div>
      )}
    </div>
  );
}

function LayerSection({
  title,
  rows,
  preset,
  onInsert,
}: {
  title: string;
  rows: PandoraAnnotation[];
  preset: TextPreset;
  onInsert: (text: string, preset: TextPreset, concatIn?: number, concatOut?: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? rows : rows.slice(0, ROWS_COLLAPSED);
  return (
    <section className="item-text-section">
      <div className="item-text-label">
        {title} <span className="item-text-count">{rows.length}</span>
      </div>
      <div className="item-text-rows">
        {shown.map((a) => {
          const snippet = stripHtml(a.value);
          if (!snippet) return null;
          return (
            <div key={a.id} className="item-text-row">
              <span className="item-text-tc timecode">{formatTime(a.in)}</span>
              <span className="item-text-snippet" title={snippet}>
                {snippet}
              </span>
              <button
                className="item-text-add small"
                title="Add as text (at matching video time if on timeline, else playhead)"
                onClick={() => onInsert(a.value, preset, a.in, a.out)}
              >
                +
              </button>
            </div>
          );
        })}
      </div>
      {rows.length > ROWS_COLLAPSED && (
        <button className="ghost item-text-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'show less' : `show all ${rows.length}`}
        </button>
      )}
    </section>
  );
}

// Section titles for layer ids that don't read well capitalised as-is.
const LAYER_TITLES: Record<string, string> = {
  machinedescriptions: 'Machine descriptions',
};

function layerTitle(id: string): string {
  return LAYER_TITLES[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
}
